import React, { useMemo } from 'react';
import { useSession } from '@/lib/SessionContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useClientPOs } from '@/hooks/useClientPOs';
import { smartDisplayQty } from '@/lib/unitConverter';
import {
  ShoppingCart, Package2, Droplets,
  ChevronRight, CheckCircle2, Clock, ArrowUpFromLine,
  TrendingDown, ShieldCheck
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import AsaAssistant from '@/components/shared/AsaAssistant';
import { toast } from 'react-hot-toast';

const fmt = (n) => n != null ? new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) : '—';

export default function PurchasingDashboard() {
  const { member } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Data Fetching ──
  const { clientPOs = [], isLoading: loadingPOs } = useClientPOs();
  const activePOs = useMemo(() => clientPOs.filter(po => {
    const st = po.statusProduksi || '';
    return !['PESANAN SELESAI', 'SELESAI', 'BATAL', 'SUDAH SAMPAI', 'DIKIRIM', 'DIKIRIM SEBAGIAN'].includes(st.toUpperCase());
  }), [clientPOs]);

  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: fullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });
  const { data: bahanCairList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: productionOrders = [] } = useQuery({ queryKey: ['production-orders'], queryFn: () => base44.entities.ProductionOrder.list() });
  const { data: tickets = [] } = useQuery({ queryKey: ['purchasing-tickets'], queryFn: () => base44.entities.PurchasingTicket.list() });

  const { data: barangMasuk = [] } = useQuery({ queryKey: ['barang-masuk'], queryFn: () => base44.entities.BarangMasuk.list('-created_date') });
  const { data: barangKeluar = [] } = useQuery({ queryKey: ['barang-keluar'], queryFn: () => base44.entities.BarangKeluar.list('-created_date') });

  const unacknowledgedMutations = useMemo(() => {
    const masuk = barangMasuk.map(m => ({ ...m, _type: 'masuk' }));
    const keluar = barangKeluar.map(m => ({ ...m, _type: 'keluar' }));
    return [...masuk, ...keluar]
      .filter(m => !m.is_acknowledged && (m.pic || '').toLowerCase() !== 'purchasing')
      .sort((a, b) => new Date(b.created_date || 0) - new Date(a.created_date || 0));
  }, [barangMasuk, barangKeluar]);

  const ackMasuk = useMutation({
    mutationFn: (id) => base44.entities.BarangMasuk.update(id, { is_acknowledged: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['barang-masuk'] })
  });
  
  const ackKeluar = useMutation({
    mutationFn: (id) => base44.entities.BarangKeluar.update(id, { is_acknowledged: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['barang-keluar'] })
  });

  const updateTicketMut = useMutation({
    mutationFn: ({ id, data }) => base44.entities.PurchasingTicket.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchasing-tickets'] })
  });

  // ── Stock Health Calculations ──
  const packagingAll = useMemo(() => [
    ...botolList.map(i => ({ ...i, _tipe: 'botol' })),
    ...tutupList.map(i => ({ ...i, _tipe: 'tutup' })),
    ...sprayList.map(i => ({ ...i, _tipe: 'spray' })),
  ], [botolList, tutupList, sprayList]);

  const packagingHealth = useMemo(() => {
    const total = packagingAll.length;
    const low = packagingAll.filter(i => i.stok != null && i.stok < 10);
    const ok = packagingAll.filter(i => i.stok != null && i.stok >= 10 && i.stok < 50);
    const good = packagingAll.filter(i => i.stok != null && i.stok >= 50);
    const empty = packagingAll.filter(i => i.stok == null || i.stok <= 0);
    const score = total > 0 ? Math.round(((good.length + ok.length * 0.5) / total) * 100) : 100;
    return { total, low, ok, good, empty, score };
  }, [packagingAll]);

  const bahanHealth = useMemo(() => {
    const total = bahanCairList.length;
    const low = bahanCairList.filter(i => i.stok != null && i.stok < 0.5);
    const ok = bahanCairList.filter(i => i.stok != null && i.stok >= 0.5 && i.stok < 2);
    const good = bahanCairList.filter(i => i.stok != null && i.stok >= 2);
    const empty = bahanCairList.filter(i => i.stok == null || i.stok <= 0);
    const score = total > 0 ? Math.round(((good.length + ok.length * 0.5) / total) * 100) : 100;
    return { total, low, ok, good, empty, score };
  }, [bahanCairList]);

  // ── Deficits (Items to Buy) ──
  const deficits = useMemo(() => {
    const required = {};

    // 1. Production Orders aktif
    const activeProductionOrders = productionOrders.filter(po => {
      if (po.deleted_at) return false;
      const st = (po.status || '').toLowerCase();
      return !['done', 'selesai', 'shipped', 'dikirim', 'batal', 'cancelled'].includes(st);
    });

    const processedPOKeys = new Set();

    activeProductionOrders.forEach(po => {
      if (po.order_number) processedPOKeys.add(String(po.order_number).trim());
      if (po.id) processedPOKeys.add(String(po.id).trim());

      const STAGES = ['pending', 'in_progress', 'diracik', 'maserasi', 'filling', 'menunggu_packing', 'packing', 'siap_kirim', 'done'];
      let stageIndex = 0;
      if (po.status) {
        stageIndex = STAGES.indexOf(po.status);
        if (stageIndex === -1) stageIndex = 0;
      }

      const addRequired = (key, type, id, qty) => {
        let needsMaterial = true;
        if (type === 'bahan-cair' && stageIndex >= 2) needsMaterial = false;
        if (['botol', 'tutup', 'spray', 'fullset'].includes(type) && stageIndex >= 4) needsMaterial = false;
        if (['item-tambahan', 'tambahan'].includes(type) && stageIndex >= 6) needsMaterial = false;
        if (!needsMaterial || qty <= 0) return;

        if (!required[key]) required[key] = { type, id, qty: 0 };
        required[key].qty += qty;
      };

      if (po.components && Array.isArray(po.components)) {
        po.components.forEach(comp => {
          let invId = comp.inventory_item_id;
          if (!invId && comp.item_name) {
            const foundFullset = fullsetList.find(f => f.nama === comp.item_name || (f.item_name && f.item_name === comp.item_name));
            if (foundFullset) invId = `fullset-${foundFullset.id}`;
            else {
              const foundBotol = botolList.find(b => b.nama === comp.item_name || (b.item_name && b.item_name === comp.item_name));
              if (foundBotol) invId = `botol-${foundBotol.id}`;
            }
          }

          if (invId) {
            let type = (comp.category === 'bibit' || comp.category === 'bahan_cair') ? 'bahan-cair' : comp.category;
            let cleanId = invId;
            const prefixMatch = invId.match(/^(botol-|tutup-|spray-|bahan-|tambahan-|fullset-)/);
            if (prefixMatch) {
              const prefix = prefixMatch[0].replace('-', '');
              cleanId = invId.replace(prefixMatch[0], '');
              if (prefix === 'fullset') type = 'fullset';
              else if (prefix === 'bahan') type = 'bahan-cair';
              else if (prefix === 'tambahan') type = 'item-tambahan';
              else type = prefix;
            }
            const key = `${type}_${cleanId}`;
            addRequired(key, type, cleanId, Number(comp.qty_needed) || 0);
          }
        });
      }
    });

    // 2. ClientPOs mandiri
    activePOs.forEach(po => {
      const poKey = String(po.id || '').trim();
      if (processedPOKeys.has(poKey)) return;

      (po.products || []).forEach(prod => {
        const qty = prod.qty || 0;
        if (prod.botol_id && !prod.botol_dari_client) {
          const key = `botol_${prod.botol_id}`;
          if (!required[key]) required[key] = { type: 'botol', id: prod.botol_id, qty: 0 };
          required[key].qty += qty;
        }
        if (prod.tutup_id && !prod.tutup_dari_client) {
          const key = `tutup_${prod.tutup_id}`;
          if (!required[key]) required[key] = { type: 'tutup', id: prod.tutup_id, qty: 0 };
          required[key].qty += qty;
        }
        if (prod.spray_id && !prod.spray_dari_client) {
          const key = `spray_${prod.spray_id}`;
          if (!required[key]) required[key] = { type: 'spray', id: prod.spray_id, qty: 0 };
          required[key].qty += qty;
        }
        let racikan = prod.racikan || [];
        if (!Array.isArray(racikan)) { try { racikan = JSON.parse(racikan); } catch(e) { racikan = []; } }
        if (!Array.isArray(racikan)) racikan = [];
        racikan.forEach(r => {
          if (r.bahan_id) {
            const key = `bahan-cair_${r.bahan_id}`;
            if (!required[key]) required[key] = { type: 'bahan-cair', id: r.bahan_id, qty: 0 };
            const totalVol = (r.volume_gram || r.volume_ml || 0) * qty;
            required[key].qty += totalVol;
          }
        });
      });
    });

    const results = [];
    Object.values(required).forEach(req => {
      let masterItem = null;
      if (req.type === 'botol') masterItem = botolList.find(i => i.id === req.id);
      if (req.type === 'tutup') masterItem = tutupList.find(i => i.id === req.id);
      if (req.type === 'spray') masterItem = sprayList.find(i => i.id === req.id);
      if (req.type === 'bahan-cair') {
        masterItem = bahanCairList.find(i => i.id === req.id);
        if (masterItem && ['kg', 'liter', 'l'].includes((masterItem.satuan || '').toLowerCase())) req.qty /= 1000;
      }
      if (masterItem) {
        const currentStock = masterItem.stok || 0;
        const ticket = tickets.find(t => String(t.material_id) === String(req.id) && t.material_type === req.type && t.status !== 'Sudah Datang');
        let deficitQty = Number((req.qty - currentStock).toFixed(2));
        if (deficitQty > 0 || ticket) {
          results.push({
            ...masterItem,
            _reqQty: Number(req.qty.toFixed(2)),
            _deficitQty: deficitQty > 0 ? deficitQty : (ticket?.qty_to_buy || 0),
            _type: req.type,
            _ticket: ticket || null,
            satuan: masterItem.satuan || 'pcs'
          });
        }
      }
    });

    return results.sort((a, b) => b._deficitQty - a._deficitQty);
  }, [activePOs, botolList, tutupList, sprayList, bahanCairList, tickets]);

  const predictions = []; // Temporarily set to empty to fix ReferenceError


  // ── Incoming Goods (Tickets being shipped) ──
  const incomingGoods = useMemo(() => {
    return tickets
      .filter(t => t.status === 'Dalam Pengiriman' || t.status === 'Sudah Dibayar' || t.status === 'Dalam Permintaan')
      .map(t => {
        const item = [...botolList, ...tutupList, ...sprayList, ...bahanCairList].find(i => i.id === t.material_id);
        return item ? { ...t, itemName: item.nama, itemSatuan: item.satuan || 'pcs' } : null;
      })
      .filter(Boolean);
  }, [tickets, botolList, tutupList, sprayList, bahanCairList]);

  // ── Asa Insights ──
  const asaInsights = useMemo(() => {
    const insights = [];

    // 1. Deficits alert (Hanya untuk item yang dibutuhkan PO aktif)
    if (deficits.length > 0) {
      const harusBeli = deficits.filter(d => (!d._ticket || d._ticket?.status === 'Harus Dibeli') && d._reqQty > 0);
      if (harusBeli.length > 0) {
        insights.push({
          id: 'deficit-alert',
          type: 'alert',
          text: `Halo Mas ${member?.full_name?.split(' ')[0] || 'Fadli'}! Ada **${harusBeli.length} item** yang harus segera dibeli karena stoknya tidak mencukupi kebutuhan PO aktif. Klik panel "*Harus Dibeli*" di bawah untuk langsung proses.`
        });
      }
    }

    // 2. Unacknowledged mutations
    if (unacknowledgedMutations.length > 0) {
      insights.push({
        id: 'unacknowledged-mutations',
        type: 'warning',
        text: `Terdapat **${unacknowledgedMutations.length} mutasi stok** (masuk/keluar) dari divisi lain. Mohon validasi di panel "Validasi Mutasi" agar pergerakan stok tetap terpantau.`
      });
    }

    // 3. Incoming goods reminder
    if (incomingGoods.length > 0) {
      insights.push({
        id: 'incoming-reminder',
        type: 'info',
        text: `Terdapat **${incomingGoods.length} pesanan** yang sedang dalam pengiriman. Jangan lupa cek dan konfirmasi ketika barang sudah tiba di gudang ya!`
      });
    }

    if (insights.length === 0) {
      insights.push({
        id: 'all-clear',
        type: 'info',
        text: `Semua stok baik-baik saja. Tidak ada kekurangan material atau barang yang perlu dipesan saat ini. Mantap!`
      });
    }

    return insights;
  }, [deficits, incomingGoods, unacknowledgedMutations, member]);

  const handleConfirmArrival = async (ticket) => {
    if (!confirm(`Konfirmasi barang "${ticket.itemName}" sudah tiba?`)) return;
    await updateTicketMut.mutateAsync({ id: ticket.id, data: { status: 'Sudah Datang' } });
    toast.success(`${ticket.itemName} dikonfirmasi sudah tiba!`);
  };

  const handleAcknowledgeMutation = (e, mut) => {
    e.stopPropagation();
    if (mut._type === 'masuk') ackMasuk.mutate(mut.id);
    else ackKeluar.mutate(mut.id);
    toast.success(`Mutasi ${mut.nomor_referensi || ''} telah divalidasi!`);
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-emerald-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-rose-600';
  };

  const getScoreBg = (score) => {
    if (score >= 75) return 'bg-emerald-50 border-emerald-200';
    if (score >= 50) return 'bg-amber-50 border-amber-200';
    return 'bg-rose-50 border-rose-200';
  };

  const getScoreLabel = (score) => {
    if (score >= 75) return 'Sehat';
    if (score >= 50) return 'Waspada';
    return 'Kritis';
  };

  return (
    <div className="space-y-8 pb-16 text-[#302621]">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black text-slate-800 tracking-tight">Dashboard Purchasing</h1>
        <p className="text-xs text-slate-500 mt-1">Pantau kesehatan stok, kebutuhan pembelian, dan kedatangan barang di gudang.</p>
      </div>

      {/* Asa Assistant */}
      <AsaAssistant
        insights={asaInsights}
        memberName={member?.full_name?.split(' ')[0]}
        title="Asa Purchasing Advisor"
      />

      {/* ── Stock Health Overview ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Packaging Health */}
        <Card className={`border overflow-hidden ${getScoreBg(packagingHealth.score)}`}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-white/80 border border-white flex items-center justify-center text-[#8C6208] shadow-sm">
                  <Package2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-black text-sm text-slate-800">Kesehatan Packaging</h3>
                  <p className="text-[10px] text-slate-500 font-medium">Botol, Tutup & Spray</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-black ${getScoreColor(packagingHealth.score)}`}>{packagingHealth.score}%</p>
                <span className={`text-[9px] font-black uppercase tracking-widest ${getScoreColor(packagingHealth.score)}`}>{getScoreLabel(packagingHealth.score)}</span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="h-2 bg-white/60 rounded-full overflow-hidden mb-4">
              <div
                className={`h-full rounded-full transition-all duration-700 ${packagingHealth.score >= 75 ? 'bg-emerald-500' : packagingHealth.score >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`}
                style={{ width: `${packagingHealth.score}%` }}
              />
            </div>

            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-white/70 rounded-xl p-2 border border-white/50">
                <p className="text-lg font-black text-slate-800">{packagingHealth.total}</p>
                <p className="text-[9px] font-bold text-slate-400 uppercase">Total</p>
              </div>
              <div className="bg-white/70 rounded-xl p-2 border border-white/50">
                <p className="text-lg font-black text-emerald-600">{packagingHealth.good.length}</p>
                <p className="text-[9px] font-bold text-slate-400 uppercase">Aman</p>
              </div>
              <div className="bg-white/70 rounded-xl p-2 border border-white/50">
                <p className="text-lg font-black text-amber-600">{packagingHealth.ok.length}</p>
                <p className="text-[9px] font-bold text-slate-400 uppercase">Sedang</p>
              </div>
              <div className="bg-white/70 rounded-xl p-2 border border-white/50">
                <p className="text-lg font-black text-rose-600">{packagingHealth.low.length + packagingHealth.empty.length}</p>
                <p className="text-[9px] font-bold text-slate-400 uppercase">Kritis</p>
              </div>
            </div>

            {packagingHealth.low.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {packagingHealth.low.slice(0, 5).map(item => (
                  <button
                    key={item.id}
                    onClick={() => navigate(`/office/purchasing/stok-detail?id=${item.id}&tipe=${item._tipe}`)}
                    className="bg-white text-rose-700 text-[10px] px-2.5 py-1 rounded-full font-bold border border-rose-200 hover:bg-rose-50 transition-colors cursor-pointer"
                  >
                    {item.nama} — {item.stok ?? 0} pcs
                  </button>
                ))}
                {packagingHealth.low.length > 5 && (
                  <span className="text-[10px] text-rose-500 font-bold px-2 py-1">+{packagingHealth.low.length - 5} lainnya</span>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bahan Cair Health */}
        <Card className={`border overflow-hidden ${getScoreBg(bahanHealth.score)}`}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-white/80 border border-white flex items-center justify-center text-blue-600 shadow-sm">
                  <Droplets className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-black text-sm text-slate-800">Kesehatan Bahan Cair</h3>
                  <p className="text-[10px] text-slate-500 font-medium">Bibit, Alkohol & Pelarut</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-black ${getScoreColor(bahanHealth.score)}`}>{bahanHealth.score}%</p>
                <span className={`text-[9px] font-black uppercase tracking-widest ${getScoreColor(bahanHealth.score)}`}>{getScoreLabel(bahanHealth.score)}</span>
              </div>
            </div>

            <div className="h-2 bg-white/60 rounded-full overflow-hidden mb-4">
              <div
                className={`h-full rounded-full transition-all duration-700 ${bahanHealth.score >= 75 ? 'bg-emerald-500' : bahanHealth.score >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`}
                style={{ width: `${bahanHealth.score}%` }}
              />
            </div>

            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-white/70 rounded-xl p-2 border border-white/50">
                <p className="text-lg font-black text-slate-800">{bahanHealth.total}</p>
                <p className="text-[9px] font-bold text-slate-400 uppercase">Total</p>
              </div>
              <div className="bg-white/70 rounded-xl p-2 border border-white/50">
                <p className="text-lg font-black text-emerald-600">{bahanHealth.good.length}</p>
                <p className="text-[9px] font-bold text-slate-400 uppercase">Aman</p>
              </div>
              <div className="bg-white/70 rounded-xl p-2 border border-white/50">
                <p className="text-lg font-black text-amber-600">{bahanHealth.ok.length}</p>
                <p className="text-[9px] font-bold text-slate-400 uppercase">Sedang</p>
              </div>
              <div className="bg-white/70 rounded-xl p-2 border border-white/50">
                <p className="text-lg font-black text-rose-600">{bahanHealth.low.length + bahanHealth.empty.length}</p>
                <p className="text-[9px] font-bold text-slate-400 uppercase">Kritis</p>
              </div>
            </div>

            {bahanHealth.low.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {bahanHealth.low.slice(0, 5).map(item => {
                  const { display, unit } = smartDisplayQty(item.stok, item.satuan);
                  return (
                    <button
                      key={item.id}
                      onClick={() => navigate(`/office/purchasing/stok-detail?id=${item.id}&tipe=bahan`)}
                      className="bg-white text-rose-700 text-[10px] px-2.5 py-1 rounded-full font-bold border border-rose-200 hover:bg-rose-50 transition-colors cursor-pointer"
                    >
                      {item.nama} — {display} {unit}
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Bottom Grid: Harus Dibeli + Prediksi Habis ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Panel: Item Harus Dibeli */}
        <Card className="border-slate-200/80 bg-white shadow-sm">
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h3 className="font-black text-sm text-slate-800 flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-rose-500" /> Harus Dibeli
              </h3>
              <Link to="/office/purchasing/items-to-buy" className="text-[10px] font-bold text-[#8C6208] hover:underline uppercase tracking-wider">
                Lihat Semua
              </Link>
            </div>
            <div className="px-5 pb-5 space-y-2 max-h-[320px] overflow-y-auto">
              {deficits.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                  <CheckCircle2 className="w-8 h-8 mb-2 text-emerald-400" />
                  <p className="text-xs font-semibold text-slate-500">Semua stok tercukupi!</p>
                </div>
              ) : (
                deficits.slice(0, 6).map(item => {
                  const { display, unit } = smartDisplayQty(item._deficitQty, item.satuan);
                  const status = item._ticket?.status || 'Harus Dibeli';
                  return (
                    <button
                      key={item.id}
                      onClick={() => navigate(`/office/purchasing/stok-detail?id=${item.id}&tipe=${item._type}`)}
                      className="w-full text-left flex items-center justify-between p-3 rounded-xl border border-slate-200/80 hover:border-rose-300 hover:bg-rose-50/30 transition-all group cursor-pointer"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-sm text-slate-800 truncate">{item.nama}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          Kurang: <span className="font-bold text-rose-600">{display} {unit}</span>
                          {item._price > 0 && <span className="ml-2 text-slate-400">· Est. Rp {fmt(item._price * item._deficitQty)}</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider border ${
                          status === 'Harus Dibeli' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                          status === 'Sudah Dibayar' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          status === 'Dalam Permintaan' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          status === 'Dalam Pengiriman' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                          'bg-amber-50 text-amber-700 border-amber-200'
                        }`}>{status}</span>
                        <ChevronRight className="w-4 h-4 text-slate-200 group-hover:text-[#8C6208] transition-colors" />
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        {/* Panel: Prediksi Habis (Velocity) */}
        <Card className="border-slate-200/80 bg-white shadow-sm">
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h3 className="font-black text-sm text-slate-800 flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-amber-500" /> Prediksi Habis
              </h3>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                7 Hari Terakhir
              </span>
            </div>
            <div className="px-5 pb-5 space-y-2 max-h-[320px] overflow-y-auto">
              {predictions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                  <ShieldCheck className="w-8 h-8 mb-2 text-emerald-400" />
                  <p className="text-xs font-semibold text-slate-500">Tidak ada item yang diprediksi habis.</p>
                </div>
              ) : (
                predictions.slice(0, 6).map(item => {
                  const { display, unit } = smartDisplayQty(item.stok, item.satuan || 'pcs');
                  return (
                    <button
                      key={item.id}
                      onClick={() => navigate(`/office/purchasing/stok-detail?id=${item.id}&tipe=${item._tipe}`)}
                      className="w-full text-left flex items-center justify-between p-3 rounded-xl border border-slate-200/80 hover:border-amber-300 hover:bg-amber-50/30 transition-all group cursor-pointer"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-sm text-slate-800 truncate">{item.nama}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          Stok: <span className="font-bold text-slate-600">{display} {unit}</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full border ${
                          item.daysLeft <= 3 ? 'bg-rose-50 text-rose-700 border-rose-200' :
                          item.daysLeft <= 7 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          'bg-blue-50 text-blue-700 border-blue-200'
                        }`}>
                          <Clock className="w-3 h-3" /> {item.daysLeft} hari
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-200 group-hover:text-[#8C6208] transition-colors" />
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
